import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  IconButton,
  Chip,
  InputAdornment,
  TextField,
  Breadcrumbs,
  Link,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Sync as SyncIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import tangoSyncService from '../services/tangoSyncService';
import Pagination from '../components/common/Pagination';

const Products = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Pagination states
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchProducts(page, searchTerm);
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [page, searchTerm]);

  const fetchProducts = async (currentPage = page, search = searchTerm) => {
    try {
      setLoading(true);
      const pageParam = `page=${currentPage + 1}`;
      const activeParam = 'is_active=true';
      const searchParam = search ? `search=${encodeURIComponent(search)}` : '';

      const queryParams = [pageParam, activeParam, searchParam].filter(Boolean).join('&');

      const response = await axios.get(`/api/products/?${queryParams}`);
      if (response.data && response.data.results) {
        setProducts(response.data.results);
        setTotalCount(response.data.count);
      } else {
        setProducts(response.data || []);
        setTotalCount(response.data ? response.data.length : 0);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await tangoSyncService.syncProducts();
      setSnackbar({
        open: true,
        message: res.message || 'Sincronización finalizada con éxito.',
        severity: 'success'
      });
      fetchProducts(page, searchTerm);
    } catch (error) {
      console.error('Error syncing products:', error);
      const errorMsg = error.response?.data?.message || 'Error al conectar con Tango ERP. Intente de nuevo.';
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error'
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const CHANNELS = [
    { field: 'is_available_for_retail', label: 'Minorista', color: { bg: '#e3f2fd', color: '#1976d2', border: '#90caf9' } },
    { field: 'is_available_for_wholesale', label: 'Mayorista', color: { bg: '#fff3e0', color: '#f57c00', border: '#ffb74d' } },
  ];

  const handleToggleChannel = async (product, field, label) => {
    const newValue = !product[field];
    // Update optimista
    setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, [field]: newValue } : p)));
    try {
      await axios.patch(`/api/products/${product.id}/`, { [field]: newValue });
      setSnackbar({
        open: true,
        message: `"${product.name}" ${newValue ? 'agregado a' : 'quitado de'} ${label}.`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating channel:', error);
      // Revertir
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, [field]: !newValue } : p)));
      setSnackbar({ open: true, message: 'No se pudo actualizar la etiqueta.', severity: 'error' });
    }
  };

  const renderChannelChips = (product) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {CHANNELS.map(({ field, label, color }) => {
        const active = !!product[field];
        return (
          <Chip
            key={field}
            label={label}
            size="small"
            onClick={() => handleToggleChannel(product, field, label)}
            sx={{
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '0.65rem',
              bgcolor: active ? color.bg : 'transparent',
              color: active ? color.color : '#9aa8b8',
              border: `1px solid ${active ? color.border : '#d0dbe7'}`,
              opacity: active ? 1 : 0.7,
              '&:hover': { bgcolor: active ? color.bg : 'rgba(0,0,0,0.04)' },
            }}
          />
        );
      })}
    </Box>
  );



  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const filteredProducts = products;

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh', bgcolor: '#f6f7f8' }}>
      <Sidebar onLogout={handleLogout} />
      <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ color: '#4e7397', fontSize: '0.875rem' }}
          >
            Inicio
          </Link>
          <Typography color="text.primary" sx={{ color: '#0e141b', fontSize: '0.875rem', fontWeight: 500 }}>
            Productos
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', mb: 4, gap: 2 }}>
          <Box>
            <Typography
              variant="h3"
              sx={{
                color: '#0e141b',
                fontSize: { xs: '1.875rem', md: '2.25rem' },
                fontWeight: 900,
                mb: 1,
                letterSpacing: '-0.033em',
              }}
            >
              Gestión de Productos
            </Typography>
            <Typography sx={{ color: '#4e7397', fontSize: '1rem' }}>
              Administra el inventario y actualiza precios.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
              onClick={handleSync}
              disabled={syncing || loading}
              sx={{
                borderColor: '#800020',
                color: '#800020',
                '&:hover': { borderColor: '#600010', bgcolor: 'rgba(128,0,32,0.04)' },
                borderRadius: '0.5rem',
                px: 3,
                py: 1.25,
                fontWeight: 700,
                textTransform: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {syncing ? 'Sincronizando...' : 'Sincronizar Tango'}
            </Button>
          </Box>
        </Box>

        {/* Products count */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>
            <span style={{ fontWeight: 700, color: '#0e141b' }}>{totalCount}</span> productos
          </Typography>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 3 }}>
          <TextField
            placeholder="Buscar producto..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#4e7397' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear search"
                    onClick={() => {
                      setSearchTerm('');
                      setPage(0);
                    }}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              width: '100%',
              maxWidth: '400px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.5rem',
                backgroundColor: '#f6f7f8',
                '& fieldset': {
                  borderColor: '#d0dbe7',
                },
                '&:hover fieldset': {
                  borderColor: '#800020',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#800020',
                },
              },
            }}
          />
        </Box>

        {/* Table / Cards */}
        <Box>
          {/* Desktop Table */}
          <TableContainer
            component={Paper}
            sx={{
              display: { xs: 'none', md: 'block' },
              borderRadius: '0.75rem',
              border: '1px solid #d0dbe7',
              boxShadow: 'none',
              overflow: 'hidden',
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f6f7f8' }}>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', py: 2 }}>Código / SKU</TableCell>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Producto</TableCell>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Canales</TableCell>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Precio (Local vs Tango)</TableCell>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Stock / Depósitos</TableCell>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Control Stock</TableCell>
                  <TableCell align="right" sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>Cargando...</TableCell>
                  </TableRow>
                ) : filteredProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>No hay productos</TableCell>
                  </TableRow>
                ) : (
                  filteredProducts.map((product) => {
                    return (
                      <TableRow key={product.id} sx={{ '&:hover': { bgcolor: '#f6f7f8' }, '& .MuiTableCell-root': { borderColor: '#f0f0f0' } }}>
                        <TableCell>
                          <Typography sx={{ fontWeight: 700, color: '#0e141b', fontSize: '0.8125rem', fontFamily: 'monospace' }}>
                            {product.cod_articulo || `PG-${product.id}`}
                          </Typography>
                          <Typography sx={{ color: '#4e7397', fontSize: '0.7rem' }}>
                            Barras: {product.tango_info?.cod_barra || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {product.image_url ? (
                              <Box component="img" src={product.image_url} alt={product.name} sx={{ width: 44, height: 44, borderRadius: '0.5rem', objectFit: 'cover' }} />
                            ) : (
                              <Box sx={{ width: 44, height: 44, borderRadius: '0.5rem', bgcolor: '#f6f7f8', border: '1px solid #d0dbe7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography sx={{ color: '#4e7397', fontSize: '0.7rem' }}>N/A</Typography>
                              </Box>
                            )}
                            <Box>
                              <Typography sx={{ color: '#0e141b', fontWeight: 700, fontSize: '0.875rem' }}>{product.name}</Typography>
                              <Typography sx={{ color: '#4e7397', fontSize: '0.75rem', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.description}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {renderChannelChips(product)}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <Typography sx={{ color: '#0e141b', fontWeight: 700, fontSize: '0.8125rem' }}>
                              Local Min/May: ${parseFloat(product.price_per_kg || 0).toLocaleString('es-AR')} / ${parseFloat(product.wholesale_price_per_kg || 0).toLocaleString('es-AR')}
                            </Typography>
                            <Typography sx={{ color: '#2e7d32', fontWeight: 600, fontSize: '0.725rem' }}>
                              Tango L1/L2: ${product.tango_info?.precio_lista_1 ? parseFloat(product.tango_info.precio_lista_1).toLocaleString('es-AR') : 'N/A'} / ${product.tango_info?.precio_lista_2 ? parseFloat(product.tango_info.precio_lista_2).toLocaleString('es-AR') : 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <Typography sx={{ color: '#0e141b', fontWeight: 800, fontSize: '0.8125rem' }}>
                              Total: {product.tango_info ? product.tango_info.total_stock : 0} {product.unit_display || 'kg'}
                            </Typography>
                            <Typography sx={{ color: '#4e7397', fontSize: '0.725rem', whiteSpace: 'nowrap' }}>
                              {product.tango_info?.stock_details?.map(d => `Dep ${d.cod_deposito}: ${d.cantidad_stock}`).join(' | ') || 'Sin stock'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {product.tango_info ? (
                            product.tango_info.stock_habilitado ? (
                              <Chip label="Controla Stock" size="small" color="success" sx={{ fontSize: '0.7rem', fontWeight: 700 }} />
                            ) : (
                              <Chip label="No Controla" size="small" color="warning" sx={{ fontSize: '0.7rem', fontWeight: 700 }} />
                            )
                          ) : (
                            <Chip label="Sin Info" size="small" variant="outlined" sx={{ fontSize: '0.7rem', fontWeight: 700 }} />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <IconButton size="small" onClick={() => navigate(`/products/${product.id}/edit`)} sx={{ color: '#1976d2' }}><EditIcon fontSize="small" /></IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile Cards */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : filteredProducts.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '1rem', border: '1px solid #d0dbe7' }}><Typography sx={{ color: '#4e7397' }}>No se encontraron productos</Typography></Paper>
            ) : (
              filteredProducts.map((product) => {
                return (
                  <Paper
                    key={product.id}
                    sx={{
                      p: 2,
                      borderRadius: '1rem',
                      border: '1px solid #d0dbe7',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {product.image_url ? (
                        <Box component="img" src={product.image_url} alt={product.name} sx={{ width: 64, height: 64, borderRadius: '0.75rem', objectFit: 'cover' }} />
                      ) : (
                        <Box sx={{ width: 64, height: 64, borderRadius: '0.75rem', bgcolor: '#f6f7f8', border: '1px dashed #d0dbe7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography sx={{ color: '#4e7397', fontSize: '0.7rem' }}>Sin foto</Typography>
                        </Box>
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0e141b' }}>{product.name}</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton size="small" onClick={() => navigate(`/products/${product.id}/edit`)} sx={{ color: '#1976d2', p: 0.5 }}><EditIcon sx={{ fontSize: '1.1rem' }} /></IconButton>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1, alignItems: 'center' }}>
                          {CHANNELS.map(({ field, label, color }) => {
                            const active = !!product[field];
                            return (
                              <Chip
                                key={field}
                                label={label}
                                size="small"
                                onClick={() => handleToggleChannel(product, field, label)}
                                sx={{
                                  cursor: 'pointer',
                                  fontWeight: 700,
                                  fontSize: '0.65rem',
                                  bgcolor: active ? color.bg : 'transparent',
                                  color: active ? color.color : '#9aa8b8',
                                  border: `1px solid ${active ? color.border : '#d0dbe7'}`,
                                  opacity: active ? 1 : 0.7,
                                }}
                              />
                            );
                          })}
                        </Box>
                        <Typography sx={{ color: '#4e7397', fontSize: '0.75rem', lineClamp: 2, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }}>{product.description}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ pt: 1.5, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography sx={{ fontSize: '0.7rem', color: '#4e7397' }}>Precio sugerido</Typography>
                        <Typography sx={{ fontWeight: 900, color: '#1b5e20', fontSize: '1.1rem' }}>${parseFloat(product.price_per_kg || 0).toLocaleString('es-AR')} <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>/ {product.unit_display?.toLowerCase() || 'kg'}</span></Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(`/products/${product.id}/edit`)}
                        sx={{ borderColor: '#d0dbe7', color: '#0e141b', textTransform: 'none', fontWeight: 700, borderRadius: '0.5rem' }}
                      >
                        Ver Ficha
                      </Button>
                    </Box>
                  </Paper>
                );
              })
            )}
          </Box>
        </Box>

        {/* Pagination */}
        {totalCount > 0 && (
          <Pagination
            count={totalCount}
            page={page}
            rowsPerPage={20}
            onPageChange={(e, newPage) => setPage(newPage)}
          />
        )}
      </Box>

      {/* Toast Alert */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Products;
